import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Row, Col } from "react-grid-system";
import DatePicker from "../../molecules/filters/DatePicker";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import TextField from "../../atoms/inputs/TextField";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { UploadIcon } from "../../atoms/icons/UploadIcon";
import { theme } from "../../../theme";
import Card from "../../atoms/Card";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText, {
  TableHeaderTextSorting,
} from "../../atoms/typography/TableHeaderText";
import TableRow from "../../atoms/TableRow";
import BodySmall from "../../atoms/typography/BodySmall";
import IconButton from "../../atoms/buttons/IconButton";
import { DownloadIcon } from "../../atoms/icons/DownloadIcon";
import Tooltip from "../../molecules/Tooltip";
import { ConnectIconSmall } from "../../atoms/icons/ConnectIcon";
import { EditIcon } from "../../atoms/icons/EditIcon";
import DeleteModal from "../../molecules/modals/DeleteModal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Modal from "../../molecules/Modal";
import UploadFile from "../../molecules/UploadFiles";
import Select from "../../atoms/inputs/Select";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";

interface Props {
  clientIdentifier: string;
}

const documentTypeOptions: { value: string; label: string }[] = [
  {
    value: "signedDocument",
    label: "clientFiles.documentTypes.signedDocument",
  },
  {
    value: "security",
    label: "clientFiles.documentTypes.security",
  },
  {
    value: "other",
    label: "clientFiles.documentTypes.other",
  },
];

const ClientDocuments: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [selectedDocumentUploadDate, setSelectedDocumentUploadDate] = useState<
    Date | undefined
  >(undefined);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<string[]>([
    "signedDocument",
    "security",
    "other",
  ]);
  const [searchDocument, setSearchDocument] = useState("");
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false);
  const [connectDocumentModalOpen, setConnectDocumentModalOpen] =
    useState(false);

  return (
    <div>
      <Row nogutter align="end" style={{ gap: 24 }}>
        <DatePicker
          onChange={setSelectedDocumentUploadDate}
          value={selectedDocumentUploadDate}
        />
        <MultiSelect
          options={documentTypeOptions}
          selectedOptions={selectedDocumentTypes}
          onChange={setSelectedDocumentTypes}
          title={t("clientFiles.documentType")}
        />
        <TextField
          placeholder={t("clientFiles.searchDocument")}
          value={searchDocument}
          onChange={setSearchDocument}
        />
        <OutlinedButton
          title={t("clientFiles.uploadDocument")}
          icon={<UploadIcon color={theme.colors.primary} />}
          color={theme.colors.primary}
          onClick={() => setUploadDocumentOpen(true)}
        />
      </Row>
      <HorizontalSpacer spacing={2} />

      <Card spacing={0} border={true}>
        <TableHeader>
          <Col lg={4}>
            <TableHeaderText>{t("clientFiles.document")}</TableHeaderText>
          </Col>
          <Col lg={2}>
            <TableHeaderTextSorting>
              {t("clientFiles.uploaded")}
            </TableHeaderTextSorting>
          </Col>
          <Col lg={2}>
            <TableHeaderText>{t("clientFiles.type")}</TableHeaderText>
          </Col>
        </TableHeader>
        <TableRow align="center">
          <Col lg={4}>
            <BodySmall>Borgensåtagande - generell</BodySmall>
          </Col>
          <Col lg={2}>
            <BodySmall>2024-06-10</BodySmall>
          </Col>
          <Col lg={2}>
            <BodySmall>Signerat dokument</BodySmall>
          </Col>
          <Col
            lg={4}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Tooltip text={t("clientFiles.downloadDocument")}>
              <IconButton
                color={theme.colors.primaryBg}
                icon={<DownloadIcon color={theme.colors.primary} />}
              />
            </Tooltip>
            <Tooltip
              text={t("clientFiles.connectDocumentToAgreementOrSecurity")}
            >
              <IconButton
                color={theme.colors.warning20}
                icon={<ConnectIconSmall color={theme.colors.warning} />}
                onClick={() => setConnectDocumentModalOpen(true)}
              />
            </Tooltip>
            <Tooltip text={t("clientFiles.editDocument")}>
              <IconButton
                color={theme.colors.info20}
                icon={<EditIcon color={theme.colors.info} />}
              />
            </Tooltip>

            <DeleteModal
              deleteButtonType="iconButtonMedium"
              onClickDelete={() => console.log("delete")}
            />
          </Col>
        </TableRow>
      </Card>

      {uploadDocumentOpen && (
        <Modal
          onClose={() => setUploadDocumentOpen(false)}
          closeIcon
          width={800}
          title={t("clientFiles.uploadDocument")}
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setUploadDocumentOpen(false),
            },
            actionButton: {
              buttonTitle: t("common.upload"),
              onClick: () => console.log("upload"),
            },
          }}
        >
          <div>
            <UploadFile
              onDrop={(file) => console.log(file)}
              text={t("clientFiles.dropFileHere")}
              multiple={false}
            />
          </div>
          <HorizontalSpacer spacing={2} />
          <Row>
            <Col lg={6}>
              <TextField
                label={t("clientFiles.documentName")}
                placeholder={t("clientFiles.documentNamePlaceholder")}
                fullWidth
              />
            </Col>
            <Col lg={6}>
              <Select
                label={t("clientFiles.documentType")}
                options={[
                  {
                    label: "clientFiles.documentTypes.signedDocument",
                    value: "signed",
                  },
                  { label: "clientFiles.documentTypes.other", value: "other" },
                ]}
                onChange={(value) => console.log(value)}
              />
            </Col>
          </Row>
        </Modal>
      )}

      {connectDocumentModalOpen && (
        <Modal
          closeIcon
          onClose={() => setConnectDocumentModalOpen(false)}
          width={800}
          title={t("clientFiles.connectDocumentToAgreementOrSecurity")}
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setConnectDocumentModalOpen(false),
            },
            actionButton: {
              buttonTitle: t("common.connect"),
              onClick: () => console.log("connect"),
            },
          }}
        >
          <HorizontalSpacer spacing={1} />
          <Row justify="center">
            <div style={{ display: "flex", gap: 16 }}>
              <RadioButtonBox
                title={t("clientFiles.agreement")}
                small
                checked
              />
              <RadioButtonBox
                title={t("clientFiles.security")}
                small
                checked={false}
              />
            </div>
          </Row>
          <HorizontalSpacer spacing={2} />

          <Row>
            <Col
              lg={6}
              style={{ display: "flex", alignItems: "flex-end", gap: 16 }}
            >
              <Select
                label={t("clientFiles.agreement")}
                options={[
                  { label: "Avtal 1", value: "agreement1" },
                  { label: "Avtal 2", value: "agreement2" },
                ]}
                onChange={(value) => console.log(value)}
              />
              <IconButton icon={<ClearIcon />} />
            </Col>
            <Col
              lg={6}
              style={{ display: "flex", alignItems: "flex-end", gap: 16 }}
            >
              <Select
                label={t("clientFiles.security")}
                options={[
                  { label: "Säkerhet 1", value: "security1" },
                  { label: "Säkerhet 2", value: "security2" },
                ]}
                onChange={(value) => console.log(value)}
                disabled={true}
              />
              <IconButton icon={<ClearIcon />} />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />
          <BodySmall color={theme.colors.primary} textalign="center">
            <Trans
              i18nKey="clientFiles.connectDocumentSummary"
              values={{
                documentName: "Dokumentnamn",
                agreementName: "Avtal 1",
              }}
              components={{ bold: <b /> }}
            />
          </BodySmall>
        </Modal>
      )}
    </div>
  );
};

export default ClientDocuments;
